import React, { useState } from "react";

const fmt = (val) =>
  Number(val || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PnLComparisonTable = ({ years }) => {
  /* ── collapse state ─────────────────────────────────────────────────── */
  const [salesOpen, setSalesOpen] = useState(false);
  const [openService, setOpenService] = useState(null);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [openExpenseCategory, setOpenExpenseCategory] = useState(null);

  /* ── expense categories (same as original PnLTable) ───────────────── */
  const expenseCategories = {
    "Cost of Services Sold (COSS)": [
      "Direct Labor",
      "Subcontractor Fees",
      "Project Supplies",
    ],
    "Operating Expenses (OpEx)": [
      "Marketing & Advertising",
      "Office Rent and Utilities",
      "Travel and Entertainment",
      "Technology & Software",
    ],
    "Administrative Expenses": [
      "Professional Fees",
      "Depreciation and Amortization",
    ],
    "Financial Expenses": ["Bank Charges"],
  };

  /* ── collect all unique service types across all years ──────────────── */
  const allServices = Array.from(
    new Set(
      years.flatMap((y) =>
        y.items.map((item) => item.item_name || "Other")
      )
    )
  );

  /* ── helper: revenue total per service per year ─────────────────────── */
  const serviceRevByYear = (service, yearData) =>
    yearData.items
      .filter((i) => (i.item_name || "Other") === service)
      .reduce((s, i) => s + i.selling_price * i.quantity, 0);

  /* ── colour helpers ─────────────────────────────────────────────────── */
  const netClass = (val) =>
    Number(val) >= 0 ? "text-success fw-bold" : "text-danger fw-bold";

  /* ── column count ───────────────────────────────────────────────────── */
  const colSpan = years.length + 1; // description + one per year

  const tdStyle = { textAlign: "right", whiteSpace: "nowrap" };
  const thStyle = { textAlign: "right", whiteSpace: "nowrap" };

  return (
    <div className="card shadow-sm">
      {/* ── header ──────────────────────────────────────────────────────── */}
      <div
        className="card-header text-white"
        style={{ background: "linear-gradient(135deg,#0d6efd,#0a58ca)" }}
      >
        <strong>P&amp;L Annual Comparison</strong>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0">
            {/* ── thead ─────────────────────────────────────────────────── */}
            <thead className="table-dark">
              <tr>
                <th style={{ minWidth: 220 }}>Description</th>
                {years.map((y) => (
                  <th key={y.year} style={thStyle}>
                    {y.year}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ════════════ SALES SECTION ════════════ */}
              <tr
                style={{ fontWeight: "bold", cursor: "pointer", background: "#e8f4ff" }}
                onClick={() => setSalesOpen(!salesOpen)}
              >
                <td>{salesOpen ? "▼" : "▶"} Sales</td>
                {years.map((y) => (
                  <td key={y.year} style={tdStyle}>
                    {fmt(y.summary.total_revenue)}
                  </td>
                ))}
              </tr>

              {/* ── service breakdown ─────────────────────────────────── */}
              {salesOpen &&
                allServices.map((svc, si) => {
                  const isOpen = openService === svc;
                  return (
                    <React.Fragment key={si}>
                      {/* service row */}
                      <tr
                        style={{ background: "#f8f9fa", cursor: "pointer" }}
                        onClick={() =>
                          setOpenService(isOpen ? null : svc)
                        }
                      >
                        <td style={{ paddingLeft: 28 }}>
                          {isOpen ? "▼" : "▶"} {svc}
                        </td>
                        {years.map((y) => (
                          <td key={y.year} style={tdStyle}>
                            {fmt(serviceRevByYear(svc, y))}
                          </td>
                        ))}
                      </tr>

                      {/* individual items */}
                      {isOpen &&
                        (() => {
                          // collect all unique customers × date for this service across years
                          const allDetails = years.flatMap((y) =>
                            y.items
                              .filter(
                                (i) => (i.item_name || "Other") === svc
                              )
                              .map((i) => ({
                                key: `${i.customer_name}-${i.date}`,
                                customer_name: i.customer_name,
                                date: i.date,
                              }))
                          );
                          const unique = Array.from(
                            new Map(allDetails.map((d) => [d.key, d])).values()
                          );

                          return unique.map((d, di) => (
                            <tr key={di} style={{ fontSize: "0.85rem" }}>
                              <td style={{ paddingLeft: 60 }}>
                                {d.customer_name} —{" "}
                                {d.date
                                  ? new Date(d.date).toLocaleDateString()
                                  : "—"}
                              </td>
                              {years.map((y) => {
                                const match = y.items.find(
                                  (i) =>
                                    (i.item_name || "Other") === svc &&
                                    i.customer_name === d.customer_name &&
                                    i.date === d.date
                                );
                                return (
                                  <td
                                    key={y.year}
                                    style={tdStyle}
                                    className={
                                      match
                                        ? match.profit >= 0
                                          ? "text-success"
                                          : "text-danger"
                                        : "text-muted"
                                    }
                                  >
                                    {match ? fmt(match.profit) : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          ));
                        })()}
                    </React.Fragment>
                  );
                })}

              {/* ════════════ EXPENSES SECTION ════════════ */}
              <tr
                style={{ fontWeight: "bold", cursor: "pointer", background: "#fff3e0" }}
                onClick={() => setExpensesOpen(!expensesOpen)}
              >
                <td>{expensesOpen ? "▼" : "▶"} Expenses</td>
                {years.map((y) => (
                  <td key={y.year} style={tdStyle}>
                    {fmt(y.summary.expenses)}
                  </td>
                ))}
              </tr>

              {/* ── expense category breakdown ─────────────────────────── */}
              {expensesOpen &&
                Object.keys(expenseCategories).map((cat, ci) => {
                  const isCatOpen = openExpenseCategory === cat;
                  return (
                    <React.Fragment key={ci}>
                      <tr
                        style={{ background: "#f8f9fa", cursor: "pointer" }}
                        onClick={() =>
                          setOpenExpenseCategory(isCatOpen ? null : cat)
                        }
                      >
                        <td style={{ paddingLeft: 28 }}>
                          {isCatOpen ? "▼" : "▶"} {cat}
                        </td>
                        {years.map((y) => (
                          <td key={y.year} style={tdStyle}>
                            {fmt(
                              expenseCategories[cat].reduce(
                                (s, item) =>
                                  s +
                                  (y.summary.expenses_breakdown?.[item] || 0),
                                0
                              )
                            )}
                          </td>
                        ))}
                      </tr>

                      {isCatOpen &&
                        expenseCategories[cat].map((item, ii) => (
                          <tr key={ii} style={{ fontSize: "0.85rem" }}>
                            <td style={{ paddingLeft: 60 }}>{item}</td>
                            {years.map((y) => (
                              <td key={y.year} style={tdStyle}>
                                {fmt(
                                  y.summary.expenses_breakdown?.[item] || 0
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}

              {/* ════════════ SUMMARY ROWS ════════════ */}
              {/* divider */}
              <tr style={{ background: "#e9ecef", pointerEvents: "none" }}>
                <td colSpan={colSpan} style={{ padding: "2px 0" }} />
              </tr>

              <tr style={{ background: "#f0fff4" }}>
                <td>
                  <strong>Total Revenue</strong>
                </td>
                {years.map((y) => (
                  <td key={y.year} style={tdStyle} className="text-success fw-bold">
                    {fmt(y.summary.total_revenue)}
                  </td>
                ))}
              </tr>

              <tr>
                <td>
                  <strong>Total Cost</strong>
                </td>
                {years.map((y) => (
                  <td key={y.year} style={tdStyle}>
                    {fmt(y.summary.total_cost)}
                  </td>
                ))}
              </tr>

              <tr style={{ background: "#f0f8ff" }}>
                <td>
                  <strong>Gross Profit</strong>
                </td>
                {years.map((y) => (
                  <td
                    key={y.year}
                    style={tdStyle}
                    className={netClass(y.summary.gross_profit)}
                  >
                    {fmt(y.summary.gross_profit)}
                  </td>
                ))}
              </tr>

              <tr>
                <td>
                  <strong>Total Expenses</strong>
                </td>
                {years.map((y) => (
                  <td key={y.year} style={tdStyle} className="text-warning fw-bold">
                    {fmt(y.summary.expenses)}
                  </td>
                ))}
              </tr>

              <tr
                style={{
                  background: "linear-gradient(90deg,#f8f9fa,#e8f4ff)",
                  borderTop: "2px solid #0d6efd",
                }}
              >
                <td>
                  <strong>Net Profit / (Loss)</strong>
                </td>
                {years.map((y) => (
                  <td
                    key={y.year}
                    style={{ ...tdStyle, fontSize: "1.05rem" }}
                    className={netClass(y.summary.net_profit)}
                  >
                    {fmt(y.summary.net_profit)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PnLComparisonTable;
